import type { WorkspaceAreaRef } from '@affine/core/modules/folder-api';
import {
  type ConnectMailAccountInput,
  MailProviderKind,
} from '@affine/graphql';
import { type I18nInstance, useI18n } from '@affine/i18n';
import {
  CheckBoxCheckLinearIcon,
  CloseIcon,
  EmailIcon,
} from '@blocksuite/icons/rc';
import {
  type CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { workspaceApiFetch } from '../../cloud';
import {
  AppResourceAreaPicker,
  AppResourceCredentialNotice,
  defaultAppResourceAreaRootFolderId,
  isAppResourceAreaSelectable,
} from '../app-resource-area-ui';
import { CollapsibleSection } from '../sidebar/components/collapsible-section';
import {
  DEFAULT_PROVIDER_OPTION,
  getProviderTileLabel,
  isMailOAuthProvider,
  MAIL_OAUTH_PROVIDER_LABELS,
  MAIL_PROVIDER_META,
  ProviderComingSoonRibbon,
} from './mail-account-providers';
import * as styles from './mail-fullscreen-view.css';
import { mailText } from './mail-i18n';
import {
  getMailPasswordProviderPreset,
  getMailProviderKindForOption,
  getMailProviderSetupHelp,
  type MailProviderOptionId,
  type MailProviderSetupHelpKind,
} from './provider-presets';
import type { MailAccountInfo } from './types';

const MAIL_OAUTH_ERROR_POPUP_BLOCKED = 'MAIL_OAUTH_POPUP_BLOCKED';
const MAIL_OAUTH_ERROR_AUTHORIZATION_FAILED = 'MAIL_OAUTH_AUTHORIZATION_FAILED';
const MAIL_OAUTH_ERROR_WINDOW_CLOSED = 'MAIL_OAUTH_WINDOW_CLOSED';
const MAIL_OAUTH_ERROR_SETUP_HTTP_PREFIX = 'MAIL_OAUTH_SETUP_HTTP_';
const MAIL_OAUTH_CALLBACK_CHANNEL_PREFIX = 'streamlain:mail-oauth-callback:';

function providerSetupHelpTextKey(
  kind: Exclude<MailProviderSetupHelpKind, 'none'>
): string {
  switch (kind) {
    case 'imap_activation_and_app_password':
      return 'accountWizard.providerHelp.imapActivationAndAppPassword';
    case 'app_password_with_2fa':
      return 'accountWizard.providerHelp.appPasswordWith2fa';
    case 'separate_mail_password':
      return 'accountWizard.providerHelp.separateMailPassword';
    case 'custom_username':
      return 'accountWizard.providerHelp.customUsername';
  }
}

function defaultMailAccountAreaRootFolderId(
  areas: readonly WorkspaceAreaRef[]
): string {
  return (
    areas.find(
      area => isAppResourceAreaSelectable(area) && area.kind === 'private'
    )?.rootFolderId ?? defaultAppResourceAreaRootFolderId(areas)
  );
}

function hasSelectableAreaRoot(
  areas: readonly WorkspaceAreaRef[],
  rootFolderId: string
): boolean {
  return areas.some(
    area =>
      area.rootFolderId === rootFolderId && isAppResourceAreaSelectable(area)
  );
}

function parseMailPort(value: string): number | null {
  const port = Number(value);
  return Number.isInteger(port) && port >= 1 && port <= 65535 ? port : null;
}

function getMailSetupErrorMessage(error: unknown, i18n: I18nInstance) {
  const message = error instanceof Error ? error.message : '';
  if (message.includes('Mail account already exists')) {
    return mailText(i18n, 'error.accountAlreadyConnected');
  }
  if (message.includes('up to 10 mail accounts')) {
    return mailText(i18n, 'error.accountLimit');
  }
  if (message.includes('Internal server error')) {
    return mailText(i18n, 'error.accountSetupCheckSettings');
  }
  if (message.includes('Mail authentication failed')) {
    return mailText(i18n, 'error.mailAuthenticationFailed');
  }
  if (message.includes('Mail connection test failed')) {
    return mailText(i18n, 'error.mailConnectionFailed');
  }
  if (message === MAIL_OAUTH_ERROR_POPUP_BLOCKED) {
    return mailText(i18n, 'error.oauthPopupBlocked');
  }
  if (message === MAIL_OAUTH_ERROR_AUTHORIZATION_FAILED) {
    return mailText(i18n, 'error.oauthAuthorizationFailed');
  }
  if (message === MAIL_OAUTH_ERROR_WINDOW_CLOSED) {
    return mailText(i18n, 'error.oauthWindowClosed');
  }
  if (message.startsWith(MAIL_OAUTH_ERROR_SETUP_HTTP_PREFIX)) {
    return mailText(i18n, 'error.oauthSetupFailedHttp', {
      status: message.slice(MAIL_OAUTH_ERROR_SETUP_HTTP_PREFIX.length),
    });
  }
  return message || mailText(i18n, 'error.accountSetupFailed');
}

export function AccountWizard({
  workspaceId,
  workspaceAreas = [],
  workspaceAreasError,
  targetAreaRootFolderId,
  initialProvider,
  showProviderPicker,
  overlayStyle,
  onClose,
  onConnect,
}: {
  workspaceId: string;
  workspaceAreas?: WorkspaceAreaRef[];
  workspaceAreasError?: string | null;
  targetAreaRootFolderId?: string;
  initialProvider?: MailProviderOptionId;
  showProviderPicker: boolean;
  overlayStyle?: CSSProperties;
  onClose: () => void;
  onConnect: (input: ConnectMailAccountInput) => Promise<MailAccountInfo>;
}) {
  const i18n = useI18n();
  const [providerOptionId, setProviderOptionId] =
    useState<MailProviderOptionId>(initialProvider ?? DEFAULT_PROVIDER_OPTION);
  const providerKind = getMailProviderKindForOption(providerOptionId);
  const selectedPasswordPreset =
    getMailPasswordProviderPreset(providerOptionId);
  const selectedProviderSetupHelp = getMailProviderSetupHelp(providerOptionId);
  const [displayName, setDisplayName] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [imapHost, setImapHost] = useState(
    selectedPasswordPreset?.imapHost ?? ''
  );
  const [imapPort, setImapPort] = useState(
    String(selectedPasswordPreset?.imapPort ?? 993)
  );
  const [imapTls, setImapTls] = useState(
    selectedPasswordPreset?.imapTls ?? true
  );
  const [smtpHost, setSmtpHost] = useState(
    selectedPasswordPreset?.smtpHost ?? ''
  );
  const [smtpPort, setSmtpPort] = useState(
    String(selectedPasswordPreset?.smtpPort ?? 465)
  );
  const [smtpTls, setSmtpTls] = useState(
    selectedPasswordPreset?.smtpTls ?? true
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showProviderSetupHelp, setShowProviderSetupHelp] = useState(false);
  const defaultAreaRootFolderId = useMemo(
    () => defaultMailAccountAreaRootFolderId(workspaceAreas),
    [workspaceAreas]
  );
  const [selectedAreaRootFolderId, setSelectedAreaRootFolderId] = useState(
    defaultAreaRootFolderId
  );
  const fixedTargetAreaRootFolderId =
    targetAreaRootFolderId &&
    hasSelectableAreaRoot(workspaceAreas, targetAreaRootFolderId)
      ? targetAreaRootFolderId
      : '';
  const effectiveAreaRootFolderId = targetAreaRootFolderId
    ? fixedTargetAreaRootFolderId
    : selectedAreaRootFolderId;
  const oauthProviderLabel = isMailOAuthProvider(providerKind)
    ? MAIL_OAUTH_PROVIDER_LABELS[providerKind]
    : '';

  useEffect(() => {
    if (!defaultAreaRootFolderId) {
      if (selectedAreaRootFolderId) {
        setSelectedAreaRootFolderId('');
      }
      return;
    }
    if (
      !selectedAreaRootFolderId ||
      !hasSelectableAreaRoot(workspaceAreas, selectedAreaRootFolderId)
    ) {
      setSelectedAreaRootFolderId(defaultAreaRootFolderId);
    }
  }, [defaultAreaRootFolderId, selectedAreaRootFolderId, workspaceAreas]);

  const applyPasswordPreset = useCallback(
    (preset: NonNullable<typeof selectedPasswordPreset>) => {
      setImapHost(preset.imapHost);
      setImapPort(String(preset.imapPort));
      setImapTls(preset.imapTls);
      setSmtpHost(preset.smtpHost);
      setSmtpPort(String(preset.smtpPort));
      setSmtpTls(preset.smtpTls);
      if (!username.trim() && emailAddress.trim()) {
        setUsername(emailAddress.trim());
      }
    },
    [emailAddress, username]
  );

  const selectProvider = (providerId: MailProviderOptionId) => {
    const provider = MAIL_PROVIDER_META.find(item => item.id === providerId);
    if (provider?.disabled) return;
    setError(null);
    setShowProviderSetupHelp(false);
    setProviderOptionId(providerId);
    const preset = getMailPasswordProviderPreset(providerId);
    if (preset) {
      applyPasswordPreset(preset);
      return;
    }
  };

  const updateEmailAddress = (value: string) => {
    const previousEmail = emailAddress.trim();
    const nextEmail = value.trim();
    setEmailAddress(value);
    if (!displayName.trim() || displayName.trim() === previousEmail) {
      setDisplayName(nextEmail);
    }
    if (
      providerKind === MailProviderKind.generic_imap_smtp &&
      (!username.trim() || username.trim() === previousEmail)
    ) {
      setUsername(nextEmail);
    }
  };

  const canSave =
    Boolean(effectiveAreaRootFolderId) &&
    (providerKind === MailProviderKind.generic_imap_smtp
      ? Boolean(
          emailAddress.trim() &&
          password &&
          imapHost.trim() &&
          smtpHost.trim() &&
          parseMailPort(imapPort) &&
          parseMailPort(smtpPort)
        )
      : Boolean(isMailOAuthProvider(providerKind) && emailAddress.trim()));

  const advancedAreaSettings = targetAreaRootFolderId ? null : (
    <div className={styles.formSection}>
      <AppResourceAreaPicker
        areas={workspaceAreas}
        disabled={isSaving}
        emptyText={mailText(i18n, 'accountWizard.areaEmpty')}
        hint={mailText(i18n, 'accountWizard.areaHint')}
        label={mailText(i18n, 'accountWizard.area')}
        selectedRootFolderId={selectedAreaRootFolderId}
        t={i18n}
        onSelectRootFolderId={setSelectedAreaRootFolderId}
      />
      {workspaceAreasError ? (
        <div className={styles.errorText}>
          {mailText(i18n, 'accountWizard.areaLoadFailed')}
        </div>
      ) : null}
      <AppResourceCredentialNotice t={i18n} />
    </div>
  );

  const save = async () => {
    setError(null);
    setShowProviderSetupHelp(false);
    const parsedImapPort = parseMailPort(imapPort);
    const parsedSmtpPort = parseMailPort(smtpPort);
    if (
      providerKind === MailProviderKind.generic_imap_smtp &&
      (!parsedImapPort || !parsedSmtpPort)
    ) {
      setError(mailText(i18n, 'accountWizard.invalidPort'));
      return;
    }
    setIsSaving(true);
    try {
      if (providerKind === MailProviderKind.generic_imap_smtp) {
        await onConnect({
          workspaceId,
          areaRootFolderId: effectiveAreaRootFolderId,
          providerKind,
          providerProfileId: providerOptionId,
          emailAddress: emailAddress.trim(),
          displayName: displayName.trim() || null,
          syncEnabled: true,
          testConnection: true,
          passwordCredential: {
            username: username.trim() || emailAddress.trim(),
            password,
            emailAddress: emailAddress.trim(),
            imapHost: imapHost.trim(),
            imapPort: parsedImapPort!,
            imapTls,
            smtpHost: smtpHost.trim(),
            smtpPort: parsedSmtpPort!,
            smtpTls,
          },
        });
      } else if (isMailOAuthProvider(providerKind)) {
        const credentialId = await startMailOAuthAuthorization({
          workspaceId,
          providerKind,
          emailAddress: emailAddress.trim(),
        });
        await onConnect({
          workspaceId,
          areaRootFolderId: effectiveAreaRootFolderId,
          providerKind,
          providerProfileId: providerOptionId,
          emailAddress: emailAddress.trim(),
          displayName: displayName.trim() || null,
          syncEnabled: true,
          credentialId,
          testConnection: true,
        });
      }
      onClose();
    } catch (err) {
      setError(getMailSetupErrorMessage(err, i18n));
      setShowProviderSetupHelp(true);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className={styles.modalOverlay}
      style={overlayStyle}
      onClick={event => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section className={styles.modal}>
        <header className={styles.modalHeader}>
          <div className={styles.modalHeaderLead}>
            <span className={styles.modalHeaderIcon}>
              <EmailIcon />
            </span>
            <div className={styles.modalTitleStack}>
              <div className={styles.modalTitle}>
                {mailText(i18n, 'accountWizard.title')}
              </div>
              <div className={styles.modalSubtitle}>
                {isMailOAuthProvider(providerKind)
                  ? mailText(i18n, 'accountWizard.subtitle.oauth', {
                      provider: oauthProviderLabel ?? '',
                    })
                  : selectedPasswordPreset
                    ? mailText(i18n, 'accountWizard.subtitle.passwordPreset', {
                        provider: selectedPasswordPreset.name,
                      })
                    : mailText(i18n, 'accountWizard.subtitle.custom')}
              </div>
            </div>
          </div>
          <button
            className={styles.iconButton}
            onClick={onClose}
            title={mailText(i18n, 'action.close')}
          >
            <CloseIcon />
          </button>
        </header>
        <div className={styles.modalBody}>
          {showProviderPicker ? (
            <div className={styles.formSection}>
              <div className={styles.formSectionTitle}>
                {mailText(i18n, 'accountWizard.provider')}
              </div>
              <div className={styles.providerTiles}>
                {MAIL_PROVIDER_META.map(provider => (
                  <button
                    key={provider.id}
                    type="button"
                    className={
                      providerOptionId === provider.id
                        ? styles.providerTileActive
                        : provider.disabled
                          ? styles.providerTileDisabled
                          : styles.providerTile
                    }
                    onClick={() => selectProvider(provider.id)}
                    disabled={provider.disabled}
                    aria-disabled={provider.disabled}
                    aria-pressed={providerOptionId === provider.id}
                  >
                    {provider.comingSoon ? <ProviderComingSoonRibbon /> : null}
                    <span className={styles.providerTileLogo}>
                      {provider.logo}
                    </span>
                    <span className={styles.providerTileName}>
                      {getProviderTileLabel(provider, i18n)}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <div className={styles.formSection}>
            <div className={styles.formGrid}>
              <label className={styles.field}>
                {mailText(i18n, 'accountWizard.displayName')}
                <input
                  className={styles.input}
                  value={displayName}
                  onChange={e => setDisplayName(e.target.value)}
                  placeholder={mailText(
                    i18n,
                    'accountWizard.displayNamePlaceholder'
                  )}
                />
              </label>
              <label className={styles.field}>
                {mailText(i18n, 'accountWizard.email')}
                <input
                  className={styles.input}
                  type="email"
                  value={emailAddress}
                  onChange={e => updateEmailAddress(e.target.value)}
                  placeholder="name@example.com"
                />
              </label>
            </div>
          </div>

          {providerKind === MailProviderKind.generic_imap_smtp ? (
            <>
              <div className={styles.formSection}>
                <div className={styles.formSectionTitle}>
                  {mailText(i18n, 'accountWizard.credentials')}
                </div>
                <div
                  className={
                    selectedPasswordPreset
                      ? styles.formGridSingle
                      : styles.formGrid
                  }
                >
                  {!selectedPasswordPreset ? (
                    <label className={styles.field}>
                      {mailText(i18n, 'accountWizard.username')}
                      <input
                        className={styles.input}
                        value={username}
                        onChange={e => setUsername(e.target.value)}
                      />
                    </label>
                  ) : null}
                  <label className={styles.field}>
                    {mailText(i18n, 'accountWizard.password')}
                    <input
                      className={styles.input}
                      type="password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                    />
                  </label>
                </div>
              </div>

              <CollapsibleSection
                title={mailText(i18n, 'accountWizard.advancedSettings')}
                subtitle={
                  selectedPasswordPreset
                    ? `IMAP ${imapHost}:${imapPort} · SMTP ${smtpHost}:${smtpPort}`
                    : targetAreaRootFolderId
                      ? undefined
                      : mailText(i18n, 'accountWizard.advancedCustomHint')
                }
                defaultCollapsed
                contentId="mail-account-advanced-settings"
              >
                <>
                  {advancedAreaSettings}
                  {selectedPasswordPreset ? (
                    <>
                      <div className={styles.formSection}>
                        <label className={styles.field}>
                          {mailText(i18n, 'accountWizard.username')}
                          <input
                            className={styles.input}
                            value={username}
                            onChange={e => setUsername(e.target.value)}
                          />
                        </label>
                      </div>
                      <div className={styles.presetSummary}>
                        <div className={styles.presetSummaryTitle}>
                          {mailText(
                            i18n,
                            'accountWizard.serverSettingsForProvider',
                            {
                              provider: selectedPasswordPreset.name,
                            }
                          )}
                        </div>
                        <div className={styles.presetSummaryText}>
                          {`IMAP ${imapHost}:${imapPort} · SMTP ${smtpHost}:${smtpPort}`}
                        </div>
                      </div>
                    </>
                  ) : null}
                  <div className={styles.formSection}>
                    <div className={styles.formSectionTitle}>
                      {mailText(i18n, 'accountWizard.incomingImap')}
                    </div>
                    <div className={styles.formGrid}>
                      <label className={styles.field}>
                        {mailText(i18n, 'accountWizard.host')}
                        <input
                          className={styles.input}
                          aria-label={`${mailText(i18n, 'accountWizard.incomingImap')} ${mailText(i18n, 'accountWizard.host')}`}
                          value={imapHost}
                          onChange={e => setImapHost(e.target.value)}
                          placeholder="imap.example.com"
                        />
                      </label>
                      <label className={styles.field}>
                        {mailText(i18n, 'accountWizard.port')}
                        <input
                          className={styles.input}
                          aria-label={`${mailText(i18n, 'accountWizard.incomingImap')} ${mailText(i18n, 'accountWizard.port')}`}
                          type="number"
                          min={1}
                          max={65535}
                          value={imapPort}
                          onChange={e => setImapPort(e.target.value)}
                        />
                      </label>
                    </div>
                    <label className={styles.checkboxField}>
                      <input
                        className={styles.checkbox}
                        aria-label={`${mailText(i18n, 'accountWizard.incomingImap')} ${mailText(i18n, 'accountWizard.useTls')}`}
                        type="checkbox"
                        checked={imapTls}
                        onChange={e => setImapTls(e.target.checked)}
                      />
                      {mailText(i18n, 'accountWizard.useTls')}
                    </label>
                  </div>

                  <div className={styles.formSection}>
                    <div className={styles.formSectionTitle}>
                      {mailText(i18n, 'accountWizard.outgoingSmtp')}
                    </div>
                    <div className={styles.formGrid}>
                      <label className={styles.field}>
                        {mailText(i18n, 'accountWizard.host')}
                        <input
                          className={styles.input}
                          aria-label={`${mailText(i18n, 'accountWizard.outgoingSmtp')} ${mailText(i18n, 'accountWizard.host')}`}
                          value={smtpHost}
                          onChange={e => setSmtpHost(e.target.value)}
                          placeholder="smtp.example.com"
                        />
                      </label>
                      <label className={styles.field}>
                        {mailText(i18n, 'accountWizard.port')}
                        <input
                          className={styles.input}
                          aria-label={`${mailText(i18n, 'accountWizard.outgoingSmtp')} ${mailText(i18n, 'accountWizard.port')}`}
                          type="number"
                          min={1}
                          max={65535}
                          value={smtpPort}
                          onChange={e => setSmtpPort(e.target.value)}
                        />
                      </label>
                    </div>
                    <label className={styles.checkboxField}>
                      <input
                        className={styles.checkbox}
                        aria-label={`${mailText(i18n, 'accountWizard.outgoingSmtp')} ${mailText(i18n, 'accountWizard.useTls')}`}
                        type="checkbox"
                        checked={smtpTls}
                        onChange={e => setSmtpTls(e.target.checked)}
                      />
                      {mailText(i18n, 'accountWizard.useTls')}
                    </label>
                  </div>
                </>
              </CollapsibleSection>
            </>
          ) : (
            <>
              <div className={styles.oauthNotice}>
                <div className={styles.oauthNoticeTitle}>
                  {mailText(i18n, 'accountWizard.oauthNoticeTitle')}
                </div>
                <div className={styles.oauthNoticeText}>
                  {mailText(i18n, 'accountWizard.oauthNoticeText', {
                    provider: oauthProviderLabel ?? '',
                  })}
                </div>
              </div>
              {advancedAreaSettings ? (
                <CollapsibleSection
                  title={mailText(i18n, 'accountWizard.advancedSettings')}
                  defaultCollapsed
                  contentId="mail-account-advanced-settings"
                >
                  {advancedAreaSettings}
                </CollapsibleSection>
              ) : null}
            </>
          )}

          {error ? <div className={styles.errorText}>{error}</div> : null}
          {showProviderSetupHelp &&
          selectedPasswordPreset &&
          selectedProviderSetupHelp &&
          selectedProviderSetupHelp.kind !== 'none' ? (
            <div
              className={styles.providerSetupHelp}
              data-testid="mail-provider-setup-help"
            >
              <div className={styles.providerSetupHelpTitle}>
                {mailText(i18n, 'accountWizard.providerHelp.title', {
                  provider: selectedPasswordPreset.name,
                })}
              </div>
              <div className={styles.providerSetupHelpText}>
                {mailText(
                  i18n,
                  providerSetupHelpTextKey(selectedProviderSetupHelp.kind),
                  { provider: selectedPasswordPreset.name }
                )}
              </div>
              {selectedProviderSetupHelp.officialHelpUrl ? (
                <a
                  className={styles.providerSetupHelpLink}
                  href={selectedProviderSetupHelp.officialHelpUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {mailText(i18n, 'accountWizard.providerHelp.openGuide', {
                    provider: selectedPasswordPreset.name,
                  })}
                </a>
              ) : null}
            </div>
          ) : null}
        </div>
        <footer className={styles.modalFooter}>
          <button className={styles.secondaryButton} onClick={onClose}>
            {mailText(i18n, 'action.cancel')}
          </button>
          <button
            className={styles.primaryButton}
            onClick={() => void save()}
            disabled={!canSave || isSaving}
          >
            <CheckBoxCheckLinearIcon />
            {isSaving
              ? isMailOAuthProvider(providerKind)
                ? mailText(i18n, 'accountWizard.connecting')
                : mailText(i18n, 'accountWizard.testing')
              : isMailOAuthProvider(providerKind)
                ? mailText(i18n, 'accountWizard.continueWithProvider', {
                    provider: oauthProviderLabel ?? '',
                  })
                : mailText(i18n, 'accountWizard.add')}
          </button>
        </footer>
      </section>
    </div>
  );
}

async function startMailOAuthAuthorization(input: {
  workspaceId: string;
  providerKind: MailProviderKind.gmail_oauth | MailProviderKind.microsoft_oauth;
  emailAddress: string;
}): Promise<string> {
  const response = await workspaceApiFetch(
    `/api/workspace/${encodeURIComponent(input.workspaceId)}/mail/oauth/authorize`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        ...input,
        callbackUrl: `${window.location.origin}/oauth/callback`,
      }),
    },
    { workspaceId: input.workspaceId, mode: 'foreground' }
  );
  const body = (await response.json().catch(() => ({}))) as {
    authorizeUrl?: string;
    credentialId?: string;
    error?: string;
    message?: string;
  };
  if (!response.ok || !body.authorizeUrl || !body.credentialId) {
    throw new Error(
      body.message ??
        body.error ??
        `${MAIL_OAUTH_ERROR_SETUP_HTTP_PREFIX}${response.status}`
    );
  }
  await completeMailOAuthPopup(body.authorizeUrl);
  return body.credentialId;
}

function getMailOAuthCallbackChannelName(authorizeUrl: string): string | null {
  try {
    const stateParam = new URL(authorizeUrl).searchParams.get('state');
    if (!stateParam) {
      return null;
    }
    const state = JSON.parse(stateParam) as {
      flow?: unknown;
      state?: unknown;
    };
    if (
      state.flow !== 'workspace-mail-oauth' ||
      typeof state.state !== 'string' ||
      !state.state
    ) {
      return null;
    }
    return `${MAIL_OAUTH_CALLBACK_CHANNEL_PREFIX}${state.state}`;
  } catch {
    return null;
  }
}

function openMailOAuthCallbackChannel(
  authorizeUrl: string
): BroadcastChannel | null {
  const channelName = getMailOAuthCallbackChannelName(authorizeUrl);
  if (!channelName || typeof BroadcastChannel === 'undefined') {
    return null;
  }
  try {
    return new BroadcastChannel(channelName);
  } catch {
    return null;
  }
}

function completeMailOAuthPopup(authorizeUrl: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const callbackChannel = openMailOAuthCallbackChannel(authorizeUrl);
    const popup = window.open(
      authorizeUrl,
      'mail-oauth-connect',
      'width=600,height=700'
    );
    if (!popup) {
      callbackChannel?.close();
      reject(new Error(MAIL_OAUTH_ERROR_POPUP_BLOCKED));
      return;
    }

    let checkClosed: number | null = null;
    let settled = false;
    const cleanup = () => {
      window.removeEventListener('message', handleMessage);
      callbackChannel?.removeEventListener('message', handleBroadcastMessage);
      callbackChannel?.close();
      if (checkClosed !== null) {
        clearInterval(checkClosed);
      }
    };
    const finish = (callback: () => void) => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      popup.close();
      callback();
    };
    const handleCallbackMessage = (data: unknown) => {
      const message = data as {
        type?: string;
        payload?: { success?: boolean; error?: string };
      };
      if (message.type !== 'oauth2-callback') return;
      finish(() => {
        if (message.payload?.success) {
          resolve();
        } else {
          reject(
            new Error(
              message.payload?.error ?? MAIL_OAUTH_ERROR_AUTHORIZATION_FAILED
            )
          );
        }
      });
    };
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      handleCallbackMessage(event.data);
    };
    const handleBroadcastMessage = (event: MessageEvent) => {
      handleCallbackMessage(event.data);
    };

    window.addEventListener('message', handleMessage);
    callbackChannel?.addEventListener('message', handleBroadcastMessage);
    checkClosed = window.setInterval(() => {
      if (popup.closed) {
        finish(() => reject(new Error(MAIL_OAUTH_ERROR_WINDOW_CLOSED)));
      }
    }, 500);
  });
}
